import { useState, useEffect, useRef } from 'react';
import { Mail, Lock, User, Loader2, Eye, EyeOff, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogoMark } from '../components/Logo';
import Turnstile from '../components/Turnstile';
import SignupRecoveryModal from '../components/SignupRecoveryModal';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { getStoredUtmParams, getStoredRef } from '../lib/attribution';
import { trackEvent } from '../lib/ga4';
import { friendlyError } from '../lib/errorReporting';

// Only require a solved Turnstile challenge when the widget is actually
// configured — matches the backend, which skips verification when its own
// TURNSTILE_SECRET_KEY isn't set (e.g. local dev without Cloudflare setup).
const TURNSTILE_REQUIRED = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);

// Deep-link support (no router — see App.js): landing page CTAs can send
// people straight into signup mode via /signup (or /register), or a
// ?mode=signup query param on any path. Defaults to login otherwise.
function wantsSignupOnLoad() {
    const path = window.location.pathname;
    const mode = new URLSearchParams(window.location.search).get('mode');
    return path === '/signup' || path === '/register' || mode === 'signup' || mode === 'register';
}

export default function Login({ onLogin }) {
    const [isRegister, setIsRegister] = useState(wantsSignupOnLoad);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ email: '', password: '', name: '' });
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef(null);

    // Forgot-password flow
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'code'
    const [forgotLoading, setForgotLoading] = useState(false);
    const [reset, setReset] = useState({ email: '', code: '', newPassword: '' });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Tick the resend cooldown down to zero.
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setInterval(() => setResendCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (TURNSTILE_REQUIRED && !turnstileToken) {
            toast.error('Completa la verificación anti-bot');
            return;
        }
        setLoading(true);
        try {
            if (isRegister) {
                if (form.password.length < 6) {
                    toast.error('La contraseña debe tener al menos 6 caracteres');
                    setLoading(false);
                    return;
                }
                const result = await api.register({
                    email: form.email,
                    password: form.password,
                    name: form.name,
                    turnstile_token: turnstileToken,
                    ...getStoredUtmParams(),
                    ref: getStoredRef(),
                });
                api.setToken(result.token);
                toast.success('¡Cuenta creada exitosamente!');
                trackEvent('sign_up', { method: 'email' });
                onLogin(result.user);
            } else {
                const result = await api.login({
                    email: form.email,
                    password: form.password,
                    turnstile_token: turnstileToken,
                });
                api.setToken(result.token);
                toast.success('¡Bienvenido de vuelta!');
                trackEvent('login', { method: 'email' });
                onLogin(result.user);
            }
        } catch (err) {
            const msg = friendlyError(err, 'Error de conexión');
            toast.error(msg);
            // Turnstile tokens are single-use — the failed attempt already
            // consumed it (even if the failure was "wrong password", not the
            // anti-bot check), so get a fresh one before the next try.
            setTurnstileToken('');
            turnstileRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    const openForgot = () => {
        setReset({ email: form.email, code: '', newPassword: '' });
        setForgotStep('email');
        setForgotOpen(true);
    };

    const closeForgot = () => {
        setForgotOpen(false);
        setForgotStep('email');
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!reset.email) return;
        setForgotLoading(true);
        try {
            const res = await api.forgotPassword(reset.email);
            toast.success(res?.message || 'Si el correo está registrado, recibirás un código.');
            setForgotStep('code');
            setResendCooldown(60);
        } catch (err) {
            const msg = friendlyError(err, 'No se pudo enviar el código');
            toast.error(msg);
        } finally {
            setForgotLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (reset.newPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        setForgotLoading(true);
        try {
            await api.resetPassword({
                email: reset.email,
                code: reset.code.trim(),
                new_password: reset.newPassword,
            });
            toast.success('Contraseña actualizada. Inicia sesión con tu nueva contraseña.');
            setForm({ email: reset.email, password: '', name: '' });
            setIsRegister(false);
            closeForgot();
        } catch (err) {
            const msg = friendlyError(err, 'No se pudo restablecer la contraseña');
            toast.error(msg);
        } finally {
            setForgotLoading(false);
        }
    };

    const subtitle = forgotOpen
        ? 'Recupera el acceso a tu cuenta'
        : isRegister
            ? 'Crea tu cuenta para comenzar'
            : 'Inicia sesión en tu cuenta';

    return (
        <div className="min-h-screen flex items-center justify-center px-4" data-testid="login-page">
            {/* Background effects */}
            {/* No blur-3xl here on purpose: Login is the very first screen a
                low/mid-end Android user hits, and filter: blur() is the
                confirmed cause of the renderer crashing to a black screen
                elsewhere in this app (see Onboarding.js, index.css
                .glass-card, Sidebar.js mobile-nav). Two of these glows ran
                at once on every load. Swapped for radial-gradient, which
                fades to transparent on its own at zero compositing cost. */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)' }} />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(91, 33, 182, 0.1) 0%, transparent 70%)' }} />
            </div>

            <div className="w-full max-w-md relative z-10">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex mb-4">
                        <LogoMark size={64} className="rounded-2xl" />
                    </div>
                    <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins' }}>
                        Mi<span className="text-violet-400">Pisto</span>
                    </h1>
                    <p className="text-slate-400 mt-2">{subtitle}</p>
                </div>

                {/* Form card */}
                <div className="glass-card rounded-2xl p-6 sm:p-8">
                    {forgotOpen ? (
                        forgotStep === 'email' ? (
                            <form onSubmit={handleSendCode} className="space-y-4" data-testid="forgot-email-form">
                                <div className="flex items-center gap-2 text-violet-400 mb-1">
                                    <KeyRound className="w-5 h-5" />
                                    <span className="font-semibold text-white">Restablecer contraseña</span>
                                </div>
                                <p className="text-sm text-slate-400">
                                    Ingresa tu correo y te enviaremos un código de verificación.
                                </p>

                                <div className="space-y-1.5">
                                    <label className="text-sm text-slate-300 font-medium">Correo electrónico</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                        <Input
                                            data-testid="forgot-email"
                                            type="email"
                                            placeholder="tu@email.com"
                                            value={reset.email}
                                            onChange={(e) => setReset({ ...reset, email: e.target.value })}
                                            required
                                            className="bg-slate-900/70 border-slate-700 pl-10 h-11 rounded-xl text-white placeholder:text-slate-400"
                                        />
                                    </div>
                                </div>

                                <Button
                                    data-testid="forgot-send-code"
                                    type="submit"
                                    disabled={forgotLoading}
                                    className="w-full h-11 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl font-semibold shadow-[0_0_20px_rgba(139,92,246,0.25)] transition-all duration-200"
                                >
                                    {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar código'}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={handleResetPassword} className="space-y-4" data-testid="forgot-reset-form">
                                <div className="flex items-center gap-2 text-violet-400 mb-1">
                                    <KeyRound className="w-5 h-5" />
                                    <span className="font-semibold text-white">Ingresa el código</span>
                                </div>
                                <p className="text-sm text-slate-400">
                                    Enviamos un código a <span className="text-slate-200">{reset.email}</span>. Ingrésalo
                                    junto con tu nueva contraseña.
                                </p>

                                <div className="space-y-1.5">
                                    <label className="text-sm text-slate-300 font-medium">Código de verificación</label>
                                    <Input
                                        data-testid="reset-code"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="6 dígitos"
                                        value={reset.code}
                                        onChange={(e) => setReset({ ...reset, code: e.target.value.replace(/\D/g, '') })}
                                        required
                                        className="bg-slate-900/70 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400 tracking-[0.4em] text-center text-lg"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-sm text-slate-300 font-medium">Nueva contraseña</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                        <Input
                                            data-testid="reset-new-password"
                                            type={showNewPassword ? 'text' : 'password'}
                                            placeholder="Mínimo 6 caracteres"
                                            value={reset.newPassword}
                                            onChange={(e) => setReset({ ...reset, newPassword: e.target.value })}
                                            required
                                            minLength={6}
                                            className="bg-slate-900/70 border-slate-700 pl-10 pr-10 h-11 rounded-xl text-white placeholder:text-slate-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                        >
                                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    data-testid="reset-submit"
                                    type="submit"
                                    disabled={forgotLoading}
                                    className="w-full h-11 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl font-semibold shadow-[0_0_20px_rgba(139,92,246,0.25)] transition-all duration-200"
                                >
                                    {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restablecer contraseña'}
                                </Button>

                                <button
                                    type="button"
                                    onClick={handleSendCode}
                                    disabled={forgotLoading || resendCooldown > 0}
                                    className="w-full text-sm text-slate-400 hover:text-violet-400 transition-colors disabled:opacity-50 disabled:hover:text-slate-400"
                                >
                                    {resendCooldown > 0 ? `Reenviar código (${resendCooldown}s)` : 'Reenviar código'}
                                </button>
                            </form>
                        )
                    ) : (
                        <>
                            {/* Login/signup switch — a tab control, not a buried link, so
                                people arriving straight from the landing page (default:
                                login mode) actually notice they can create an account. */}
                            <div className="grid grid-cols-2 gap-1 p-1 mb-6 bg-slate-900/70 border border-slate-700 rounded-xl">
                                <button
                                    type="button"
                                    data-testid="tab-login"
                                    onClick={() => {
                                        setIsRegister(false);
                                        setForm({ email: '', password: '', name: '' });
                                    }}
                                    className={`hit-44 h-9 rounded-lg text-sm font-semibold transition-all duration-200 ${!isRegister
                                            ? 'bg-violet-500 text-on-brand shadow-[0_0_20px_rgba(139,92,246,0.25)]'
                                            : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                >
                                    Iniciar sesión
                                </button>
                                <button
                                    type="button"
                                    data-testid="tab-register"
                                    onClick={() => {
                                        setIsRegister(true);
                                        setForm({ email: '', password: '', name: '' });
                                    }}
                                    className={`hit-44 h-9 rounded-lg text-sm font-semibold transition-all duration-200 ${isRegister
                                            ? 'bg-violet-500 text-on-brand shadow-[0_0_20px_rgba(139,92,246,0.25)]'
                                            : 'text-slate-400 hover:text-slate-200'
                                        }`}
                                >
                                    Crear cuenta
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {isRegister && (
                                    <div className="space-y-1.5">
                                        <label className="text-sm text-slate-300 font-medium">Nombre</label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                            <Input
                                                data-testid="register-name"
                                                type="text"
                                                placeholder="Tu nombre"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                className="bg-slate-900/70 border-slate-700 pl-10 h-11 rounded-xl text-white placeholder:text-slate-400"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-1.5">
                                    <label className="text-sm text-slate-300 font-medium">Correo electrónico</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                        <Input
                                            data-testid="login-email"
                                            type="email"
                                            placeholder="tu@email.com"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            required
                                            className="bg-slate-900/70 border-slate-700 pl-10 h-11 rounded-xl text-white placeholder:text-slate-400"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-sm text-slate-300 font-medium">Contraseña</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                        <Input
                                            data-testid="login-password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder={isRegister ? 'Mínimo 6 caracteres' : '••••••••'}
                                            value={form.password}
                                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            required
                                            minLength={isRegister ? 6 : 1}
                                            className="bg-slate-900/70 border-slate-700 pl-10 pr-10 h-11 rounded-xl text-white placeholder:text-slate-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {!isRegister && (
                                        <div className="flex justify-end pt-1">
                                            <button
                                                type="button"
                                                data-testid="forgot-password-link"
                                                onClick={openForgot}
                                                className="text-xs text-slate-400 hover:text-violet-400 transition-colors"
                                            >
                                                ¿Olvidaste tu contraseña?
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {TURNSTILE_REQUIRED && (
                                    <Turnstile
                                        ref={turnstileRef}
                                        onVerify={setTurnstileToken}
                                        onExpire={() => setTurnstileToken('')}
                                        onError={() => setTurnstileToken('')}
                                    />
                                )}

                                <Button
                                    data-testid="login-submit"
                                    type="submit"
                                    disabled={loading || (TURNSTILE_REQUIRED && !turnstileToken)}
                                    className="w-full h-11 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl font-semibold shadow-[0_0_20px_rgba(139,92,246,0.25)] transition-all duration-200"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : isRegister ? (
                                        'Crear cuenta'
                                    ) : (
                                        'Iniciar sesión'
                                    )}
                                </Button>
                            </form>
                        </>
                    )}

                    {forgotOpen && (
                        <div className="mt-6 text-center">
                            <button
                                type="button"
                                data-testid="forgot-back"
                                onClick={closeForgot}
                                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-violet-400 transition-colors duration-200"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Volver a iniciar sesión
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Signup-abandon recovery — independent of the form above, see
                SignupRecoveryModal for why it's safe to leave always-mounted. */}
            <SignupRecoveryModal active={isRegister && !forgotOpen} prefillEmail={form.email} />
        </div>
    );
}
